/**
 * AdminMapeoStages · /admin/mapeo-stages
 * v3.9.7 — Mapeo manual stage_id_odoo → stage_name legible.
 * Alternativa cuando el DBA aún no otorga SELECT en public.project_task_type,
 * o cuando el sync Odoo está desactivado (preview). Sin este mapeo, la Bandeja
 * OT SMART cae al `subscription_stage_name` (típicamente "Activo") y no se
 * puede clasificar por estado real del task.
 */
import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import { Layers, RefreshCw, Save, Loader2, ChevronRight, AlertCircle, Filter } from "lucide-react";

// Presets rápidos por categoría (los nombres canónicos de Odoo project.task.type)
const STAGE_PRESETS = [
  { name: "Nuevo",         category: "operativo" },
  { name: "Por atender",   category: "operativo" },
  { name: "Agendado",      category: "operativo" },
  { name: "Confirmado",    category: "operativo" },
  { name: "En Curso",      category: "operativo" },
  { name: "Viajando",      category: "operativo" },
  { name: "En Sitio",      category: "operativo" },
  { name: "Hecho",         category: "cerrado" },
  { name: "Terminado",     category: "cerrado" },
  { name: "Cancelado",     category: "cerrado" },
  { name: "Fallido",       category: "cerrado" },
  { name: "Suspendido",    category: "otro" },
];

const CATEGORY_COLORS = {
  operativo: { bg: "#DBEAFE", fg: "#1E40AF", border: "#93C5FD" },
  cerrado:   { bg: "#D1FAE5", fg: "#065F46", border: "#6EE7B7" },
  otro:      { bg: "#F3F4F6", fg: "#374151", border: "#D1D5DB" },
};

export default function AdminMapeoStages() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/v1/mesa/stage-map");
      setItems(data.items || []);
    } catch (e) { toast.error("Error cargando mapeo de stages"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const setMapping = async (stage_id, stage_name, category) => {
    setSaving(stage_id);
    try {
      if (!stage_name) {
        await api.delete(`/v1/mesa/stage-map/${stage_id}`);
        toast.success(`Mapeo stage ${stage_id} eliminado`);
      } else {
        await api.put("/v1/mesa/stage-map", { stage_id, stage_name, category });
        toast.success(`Stage ${stage_id} = ${stage_name}`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando");
    } finally { setSaving(null); }
  };

  const filtered = q.trim()
    ? items.filter((i) => JSON.stringify(i).toLowerCase().includes(q.trim().toLowerCase()))
    : items;

  const mapped = items.filter((i) => i.mapped_name).length;
  const totalOts = items.reduce((acc, i) => acc + Number(i.ot_count || 0), 0);
  const mappedOts = items.filter((i) => i.mapped_name)
    .reduce((acc, i) => acc + Number(i.ot_count || 0), 0);

  return (
    <Layout>
      <div className="space-y-4" data-testid="stage-mapping-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full mb-2" style={{ background: "#DBEAFE", border: "1px solid #93C5FD" }}>
              <Layers size={11} className="text-blue-700"/>
              <span className="text-[10px] font-semibold uppercase tracking-widest text-blue-800">Diagnóstico · Mapeo de Estados (Stages)</span>
            </div>
            <h1 className="text-3xl font-bold" style={{ color: "#0A3747" }}>Stages Odoo → Estados AlfaNet</h1>
            <p className="mt-1 text-sm text-gray-500 max-w-3xl">
              Sin este mapeo la Bandeja OT SMART no clasifica correctamente por Stage y cae al{" "}
              <code className="text-xs px-1 py-0.5 rounded bg-gray-100">subscription_stage_name</code>{" "}
              (típicamente &quot;Activo&quot;). Aquí nombras cada <code className="text-xs px-1 py-0.5 rounded bg-gray-100">stage_id_odoo</code> presente en <code className="text-xs px-1 py-0.5 rounded bg-gray-100">master_ot</code>.
            </p>
            <p className="mt-2 text-xs text-gray-500">
              Stages distintos: <b>{items.length}</b> · Mapeados: <b className="text-emerald-700">{mapped}</b> · Pendientes: <b className="text-red-700">{items.length - mapped}</b>
              {" · "}Cobertura OTs: <b className="text-emerald-700">{mappedOts.toLocaleString("es-EC")}</b> / {totalOts.toLocaleString("es-EC")} ({totalOts ? Math.round(100 * mappedOts / totalOts) : 0}%)
            </p>
          </div>
          <button onClick={load} className="neu-btn" data-testid="btn-refresh"><RefreshCw size={14}/> Refrescar</button>
        </div>

        <IntakeStagesCard />

        <div className="rounded-lg p-3 border" style={{ background: "#FEF3C7", borderColor: "#FBBF24" }}>
          <div className="flex items-start gap-2">
            <AlertCircle size={14} className="text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="text-[11px] text-amber-900">
              <b>Contexto técnico:</b> Odoo expone los nombres de stages en <code>public.project_task_type</code>. El sync Live-Sync intenta leerlos automáticamente,
              pero requiere <b>SELECT grant</b> del DBA. Mientras eso no se otorgue (o en preview sin acceso a Odoo PG), este mapeo manual permite operar sin bloqueos.
              Cuando el sync obtenga los nombres reales, éstos tendrán prioridad sobre el mapeo manual.
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por stage_id o nombre..."
            className="flex-1 min-w-[240px] px-3 py-2 border rounded text-sm"
            data-testid="search-input"
          />
        </div>

        {loading ? (
          <div className="text-center py-12"><Loader2 size={20} className="animate-spin"/></div>
        ) : (
          <div className="neu-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest font-bold text-gray-500">Stage ID</th>
                    <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest font-bold text-gray-500">OTs</th>
                    <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest font-bold text-gray-500">Sub. Stage (contexto)</th>
                    <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest font-bold text-gray-500">Nombre asignado</th>
                    <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest font-bold text-gray-500">Categoría</th>
                    <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest font-bold text-gray-500">Modificado por</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((it) => (
                    <StageRow
                      key={it.stage_id}
                      item={it}
                      saving={saving === it.stage_id}
                      onSave={setMapping}
                    />
                  ))}
                </tbody>
              </table>
              {filtered.length === 0 && (
                <div className="text-center py-8 text-gray-400 text-sm">
                  Sin stages para mostrar. La tabla se pobla cuando el sync Odoo materializa <code>master_ot</code> con <code>stage_id_odoo</code>.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function StageRow({ item, saving, onSave }) {
  const [name, setName] = useState(item.mapped_name || "");
  const [category, setCategory] = useState(item.mapped_category || "operativo");
  const isMapped = !!item.mapped_name;

  useEffect(() => {
    setName(item.mapped_name || "");
    setCategory(item.mapped_category || "operativo");
  }, [item.mapped_name, item.mapped_category]);

  const dirty = (name || "") !== (item.mapped_name || "") || (category || "") !== (item.mapped_category || "operativo");
  const catStyle = CATEGORY_COLORS[category] || CATEGORY_COLORS.otro;

  return (
    <tr className={`border-t ${!isMapped ? "bg-red-50/30" : ""}`} data-testid={`row-stage-${item.stage_id}`}>
      <td className="px-3 py-1.5 font-mono text-xs font-bold text-[#0A3747]">{item.stage_id}</td>
      <td className="px-3 py-1.5 text-right tabular-nums font-mono text-xs">
        <span className="font-semibold">{Number(item.ot_count || 0).toLocaleString("es-EC")}</span>
      </td>
      <td className="px-3 py-1.5 text-xs text-gray-500">
        {item.subscription_stage_name || "—"}
      </td>
      <td className="px-3 py-1.5">
        <div className="flex items-center gap-1.5 flex-wrap">
          <input
            type="text" value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ej. Nuevo, Agendado…"
            list={`stage-presets-${item.stage_id}`}
            className={`px-2 py-1 border rounded text-xs w-40 ${isMapped ? "border-emerald-300 bg-emerald-50" : "border-red-300 bg-red-50"}`}
            data-testid={`input-name-${item.stage_id}`}
          />
          <datalist id={`stage-presets-${item.stage_id}`}>
            {STAGE_PRESETS.map((p) => <option key={p.name} value={p.name} />)}
          </datalist>
        </div>
      </td>
      <td className="px-3 py-1.5">
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="px-2 py-1 border rounded text-xs font-semibold"
          style={{
            background: catStyle.bg,
            color: catStyle.fg,
            borderColor: catStyle.border,
          }}
          data-testid={`select-cat-${item.stage_id}`}
        >
          <option value="operativo">Operativo</option>
          <option value="cerrado">Cerrado</option>
          <option value="otro">Otro</option>
        </select>
      </td>
      <td className="px-3 py-1.5 text-[10px] text-gray-500">
        <div className="flex items-center gap-2">
          <span className="font-mono">{item.mapped_by || "—"}</span>
          {dirty && (
            <button
              onClick={() => onSave(item.stage_id, name.trim(), category)}
              disabled={saving}
              className="px-2 py-0.5 rounded text-white text-[10px] font-bold flex items-center gap-1"
              style={{ background: "#0A3747" }}
              data-testid={`btn-save-${item.stage_id}`}
            >
              {saving ? <Loader2 size={10} className="animate-spin"/> : <Save size={10}/>} Guardar
            </button>
          )}
          {isMapped && !dirty && (
            <button
              onClick={() => onSave(item.stage_id, "", category)}
              disabled={saving}
              className="text-[10px] text-red-600 hover:underline"
              data-testid={`btn-clear-${item.stage_id}`}
            >
              Limpiar
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

// v99.3.0 · Etapas de ENTRADA desde Odoo: solo estas OTs ingresan a AlfaField (p. ej. Nuevo y Asignado).
function IntakeStagesCard() {
  const [d, setD] = useState(null);
  const [sel, setSel] = useState([]);
  const [saving, setSaving] = useState(false);
  const load = async () => {
    try {
      const { data } = await api.get("/v1/admin/odoo-intake/stages");
      setD(data); setSel(data.selected_ids || []);
    } catch (e) { toast.error("No se pudieron cargar las etapas de entrada"); }
  };
  useEffect(() => { load(); }, []);
  const toggle = (id) => setSel((x) => (x.includes(id) ? x.filter((i) => i !== id) : [...x, id]));
  const save = async () => {
    setSaving(true);
    try {
      const names = Object.fromEntries((d?.items || []).filter((i) => sel.includes(i.stage_id)).map((i) => [i.stage_id, i.name || null]));
      const { data } = await api.put("/v1/admin/odoo-intake/stages", { stage_ids: sel, names });
      toast.success(data.selected_ids.length ? `Solo ingresarán OTs de ${data.selected_ids.length} etapa(s)` : "Ingresarán OTs de todas las etapas activas");
      load();
    } catch (e) { toast.error(String(e?.response?.data?.detail || "Error al guardar")); }
    finally { setSaving(false); }
  };
  const dirty = d && JSON.stringify([...sel].sort((a, b) => a - b)) !== JSON.stringify(d.selected_ids || []);
  return (
    <div className="rounded-xl p-4 border-2" style={{ borderColor: "#0A3747", background: "#F8FAFC" }} data-testid="intake-stages-card">
      <div className="flex items-start gap-2 flex-wrap">
        <Filter size={16} className="text-[#0A3747] mt-0.5" />
        <div className="flex-1 min-w-[260px]">
          <div className="font-bold text-[#0A3747]">Etapas de entrada desde Odoo</div>
          <div className="text-xs text-gray-600">
            Solo las OTs en las etapas marcadas <b>ingresan</b> a AlfaField (p. ej. <b>Nuevo</b> y <b>Asignado</b>).
            Las OTs que ya están en AlfaField siguen actualizándose aunque cambien de etapa en Odoo. Cerradas y canceladas nunca ingresan.
          </div>
          <div className="text-[11px] mt-1">
            Modo actual: <b>{d?.mode === "solo_etapas_marcadas" ? "solo etapas marcadas" : "todas las etapas activas"}</b>
            {d?.env_override && <span className="text-amber-700"> · la variable ODOO_ALLOWED_STAGE_IDS del servidor tiene prioridad sobre esta selección</span>}
          </div>
        </div>
        <button onClick={save} disabled={!dirty || saving} className="neu-btn" data-testid="intake-save">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Guardar
        </button>
      </div>
      {!d && <div className="text-xs text-gray-500 mt-2 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Cargando etapas…</div>}
      {d && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-3">
          {(d.items || []).map((i) => (
            <label key={i.stage_id} data-testid={`intake-stage-${i.stage_id}`}
              className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer ${sel.includes(i.stage_id) ? "bg-[#0A3747] text-white border-[#0A3747]" : "bg-white border-gray-200"}`}>
              <input type="checkbox" checked={sel.includes(i.stage_id)} onChange={() => toggle(i.stage_id)} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{i.name || `Etapa ${i.stage_id}`} <span className="opacity-60 font-mono text-[10px]">#{i.stage_id}</span></div>
                <div className="text-[10px] opacity-75">
                  {i.odoo_active_tasks != null ? `${i.odoo_active_tasks.toLocaleString("es-EC")} en Odoo` : "conteo Odoo no disponible"}
                  {" · "}{(i.in_alfafield || 0).toLocaleString("es-EC")} en AlfaField
                </div>
              </div>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
